import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { TRACK_PAGE_VIEW, UPDATE_PAGE_VIEW, TRACK_EVENT } from '../queries';

// Generate or retrieve a visitor ID (persists for 2 years)
const getVisitorId = (): string => {
  const key = 'bb_visitor_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem(key, id);
  }
  return id;
};

// Generate or retrieve a session ID (expires after 30 minutes of inactivity)
const getSessionId = (): string => {
  const key = 'bb_session_id';
  const expKey = 'bb_session_exp';
  const sessionDuration = 30 * 60 * 1000; // 30 minutes

  const now = Date.now();
  const exp = parseInt(sessionStorage.getItem(expKey) || '0', 10);
  let id = sessionStorage.getItem(key);

  if (!id || now > exp) {
    // Session expired or doesn't exist
    id = 's_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem(key, id);
  }

  // Extend session
  sessionStorage.setItem(expKey, (now + sessionDuration).toString());
  return id;
};

// Get UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    utmTerm: params.get('utm_term') || undefined,
    utmContent: params.get('utm_content') || undefined,
  };
};

interface AnalyticsTrackerProps {
  enabled?: boolean;
}

/**
 * AnalyticsTracker Component
 *
 * Add this component once in your App.tsx to automatically track:
 * - Page views on route changes
 * - Time on page and scroll depth
 * - Custom events via the trackEvent function
 *
 * Usage:
 * <AnalyticsTracker enabled={true} />
 *
 * The component respects Do Not Track (DNT) browser settings.
 */
export const AnalyticsTracker: React.FC<AnalyticsTrackerProps> = ({ enabled = true }) => {
  const location = useLocation();
  const pageViewIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const maxScrollDepthRef = useRef<number>(0);

  const [trackPageViewMutation] = useMutation(TRACK_PAGE_VIEW);
  const [updatePageViewMutation] = useMutation(UPDATE_PAGE_VIEW);

  // Check DNT
  const doNotTrack = navigator.doNotTrack === '1' || (window as any).doNotTrack === '1';

  // Track scroll depth
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
    maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, Math.min(100, scrollPercent));
  }, []);

  // Send page view update before leaving
  const sendPageUpdate = useCallback(async () => {
    if (!pageViewIdRef.current || !enabled) return;

    const timeOnPage = Math.floor((Date.now() - startTimeRef.current) / 1000);

    try {
      await updatePageViewMutation({
        variables: {
          input: {
            pageViewId: pageViewIdRef.current,
            timeOnPage,
            scrollDepth: maxScrollDepthRef.current,
          },
        },
      });
    } catch (error) {
      // Silently fail - analytics should not break the app
      console.debug('Analytics update failed:', error);
    }
  }, [enabled, updatePageViewMutation]);

  // Track page view on route change
  useEffect(() => {
    if (!enabled) return;

    const trackView = async () => {
      // Send update for previous page
      await sendPageUpdate();

      // Reset tracking for new page
      startTimeRef.current = Date.now();
      maxScrollDepthRef.current = 0;
      pageViewIdRef.current = null;

      const utm = getUTMParams();

      try {
        const result = await trackPageViewMutation({
          variables: {
            input: {
              pagePath: location.pathname,
              pageTitle: document.title,
              visitorId: getVisitorId(),
              sessionId: getSessionId(),
              referrer: document.referrer || undefined,
              ...utm,
              userAgent: navigator.userAgent,
              screenWidth: window.screen.width,
              screenHeight: window.screen.height,
              doNotTrack,
            },
          },
        });

        pageViewIdRef.current = result.data?.trackPageView?.id || null;
      } catch (error) {
        // Silently fail
        console.debug('Analytics track failed:', error);
      }
    };

    trackView();

    // Set up scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Clean up and send final update on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname, enabled, trackPageViewMutation, sendPageUpdate, handleScroll, doNotTrack]);

  // Send update before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      sendPageUpdate();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sendPageUpdate]);

  return null; // This component doesn't render anything
};

/**
 * Hook to track custom events
 *
 * Usage:
 * const trackEvent = useAnalyticsEvent();
 * trackEvent('button_click', 'navigation', 'signup_button');
 */
export const useAnalyticsEvent = () => {
  const location = useLocation();
  const [trackEventMutation] = useMutation(TRACK_EVENT);

  const trackEvent = useCallback(
    async (
      eventName: string,
      category?: string,
      label?: string,
      value?: number,
      metadata?: Record<string, any>
    ) => {
      try {
        await trackEventMutation({
          variables: {
            input: {
              eventName,
              category,
              label,
              value,
              pagePath: location.pathname,
              pageTitle: document.title,
              visitorId: getVisitorId(),
              sessionId: getSessionId(),
              metadata: metadata ? JSON.stringify(metadata) : undefined,
              deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
              browser: getBrowserName(),
            },
          },
        });
      } catch (error) {
        console.debug('Analytics event failed:', error);
      }
    },
    [location.pathname, trackEventMutation]
  );

  return trackEvent;
};

// Helper to get browser name
const getBrowserName = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Opera')) return 'Opera';
  return 'Unknown';
};

export default AnalyticsTracker;
